import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { PencilSquareIcon, ArchiveBoxIcon, TrashIcon, InboxIcon } from '@heroicons/react/24/outline';

const DiscipleNotes = ({ discipleId, userAppID, onNoteAdded, showArchived: showArchivedProp }) => {
  const [notes, setNotes] = useState([]);
  const [archivedNotes, setArchivedNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(showArchivedProp || false);

  const loadNotes = async () => {
    if (discipleId && userAppID) {
      try {
        console.log('Loading notes with:', { discipleId, userAppID });
        const mentorNotesRef = collection(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'notes');
        const notesSnapshot = await getDocs(mentorNotesRef);
        
        const fetchedNotes = notesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const sortedNotes = fetchedNotes.sort((a, b) => {
          const timeA = a.createdAt?.seconds || 0;
          const timeB = b.createdAt?.seconds || 0;
          return timeB - timeA;
        });

        console.log('Fetched notes:', sortedNotes);
        setNotes(sortedNotes.filter(note => !note.archived));
        setArchivedNotes(sortedNotes.filter(note => note.archived));
      } catch (error) {
        console.error('Error loading notes:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadNotes();
  }, [discipleId, userAppID]);

  // If parent changes showArchived prop, update local state
  useEffect(() => {
    if (typeof showArchivedProp === 'boolean') setShowArchived(showArchivedProp);
  }, [showArchivedProp]);

  const handleAddNote = async (noteText) => {
    if (newNote.trim() === '') return;

    try {
      const mentorNotesRef = collection(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'notes');
      await addDoc(mentorNotesRef, {
        note: newNote,
        createdAt: new Date(),
        createdBy: userAppID,
        archived: false,
      });

      setNewNote('');
      loadNotes(); // Refresh notes after adding

      console.log('🟢 DiscipleNotes - Note added, calling onNoteAdded handler');
      if (onNoteAdded) {
        onNoteAdded();
      } else {
        console.warn('🟢 DiscipleNotes - No onNoteAdded handler provided');
      }
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleArchiveNote = async (noteId, shouldArchive) => {
    try {
      const noteRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'notes', noteId);
      await updateDoc(noteRef, { archived: shouldArchive });
      loadNotes(); // Refresh notes after archiving
    } catch (error) {
      console.error('Error archiving note:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const noteRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'notes', noteId);
      await deleteDoc(noteRef);
      loadNotes(); // Refresh notes after deletion
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  if (isLoading) {
    return <div className="animate-pulse">Loading notes...</div>;
  }

  const notesToDisplay = showArchived ? archivedNotes : notes;

  return (
    <div className="max-w-xl mx-auto px-2 py-4">

      {/* Add Note Card */}
      <div className="mb-6 bg-white rounded-2xl shadow-lg border-2 border-blue-100 p-4 flex flex-col items-center">
      <div className="relative w-full">
  <textarea
    value={newNote}
    onChange={(e) => setNewNote(e.target.value)}
    className="w-full p-3 pr-10 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
    placeholder="Enter your note here..."
    rows="4"
  />
  <PencilSquareIcon className="absolute top-3 right-3 h-5 w-5 text-blue-400 pointer-events-none" />
</div>

        <div className="flex justify-end w-full mt-3">
          <button
            onClick={() => handleAddNote(newNote)}
            className="px-5 py-2 bg-[#0076BE] text-white rounded-lg shadow font-semibold hover:bg-blue-700 text-sm transition"
          >
            Add Note
          </button>
        </div>
      </div>

      {/* Toggle Archived/Active Notes Link */}
      <div className="flex justify-center mb-4">
        <button
          className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full border ${showArchived ? 'border-blue-400 text-blue-600 bg-blue-50' : 'border-gray-300 text-gray-500 bg-white'} shadow-sm hover:bg-blue-100 transition`}
          onClick={() => setShowArchived((prev) => !prev)}
        >
          <ArchiveBoxIcon className="h-4 w-4" />
          {showArchived ? 'View Active Notes' : 'View Archived Notes'}
        </button>
      </div>

      {/* Notes List — clean cards, no left bar; responsive footer */}
      <div className="space-y-3 sm:space-y-4">
        {notesToDisplay.map((note) => (
          <div
            key={note.id}
            className={`p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-white border border-gray-100 shadow-sm flex flex-col min-w-0 ${note.archived ? 'opacity-80' : ''}`}
          >
            <p className="mb-3 text-gray-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words">{note.note}</p>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="text-gray-500 font-medium">
                {note.createdAt?.toDate?.() ? note.createdAt.toDate().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
              </span>
              <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                <button
                  onClick={() => handleArchiveNote(note.id, !note.archived)}
                  className="flex items-center gap-1 text-amber-600 hover:text-amber-800 px-2 py-1.5 rounded-lg hover:bg-amber-50 transition"
                  title={note.archived ? 'Unarchive' : 'Archive'}
                >
                  <ArchiveBoxIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{note.archived ? 'Unarchive' : 'Archive'}</span>
                </button>
                <button
                  onClick={() => handleDeleteNote(note.id)}
                  className="flex items-center gap-1 text-red-600 hover:text-red-800 px-2 py-1.5 rounded-lg hover:bg-red-50 transition"
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </div>
            </div>
          </div>
        ))}
        {notesToDisplay.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <InboxIcon className="h-12 w-12 mb-2" />
            <p className="text-base">No notes yet. Start by adding your first observation!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscipleNotes;
